import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import DashboardCard from "./DashboardCard";
import FloatingCard from "./FloatingCard";

export default function HeroVisual() {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  return (
    <div
      className="relative mx-auto h-[380px] w-full max-w-[480px] sm:h-[480px] lg:h-[560px]"
      aria-hidden="true"
    >
      {/* Orb — چرخان */}
      <motion.div
        animate={reduce ? {} : { rotate: 360 }}
        transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 m-auto h-[260px] w-[260px] rounded-full opacity-25 blur-[70px] sm:h-[360px] sm:w-[360px]"
        style={{
          background:
            "conic-gradient(from 180deg, var(--teal), var(--teal-2), var(--gold-2), var(--teal))",
        }}
      />

      {/* Ring 1 — dashed */}
      <motion.div
        animate={reduce ? {} : { rotate: -360 }}
        transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 m-auto h-[240px] w-[240px] rounded-full border border-dashed border-teal-2/40 sm:h-[320px] sm:w-[320px]"
      >
        <span className="absolute -top-[5px] left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-gold-2 shadow-[0_0_20px_var(--gold-2)]" />
      </motion.div>

      {/* Ring 2 — solid thin */}
      <div className="absolute inset-0 m-auto h-[200px] w-[200px] rounded-full border border-gold/20 sm:h-[280px] sm:w-[280px]" />

      {/* Dashboard */}
      <DashboardCard />

      {/* Chat bubbles */}
      <BotBubble text={t("chat.processing")} />
      <UserBubble text={t("chat.userMsg")} />

      {/* Floating cards */}
      <FloatingCard
        icon="⚡"
        tone="teal"
        title={t("float1.title")}
        sub={t("float1.sub")}
        className="top-[4%] end-[-12px] sm:!end-[-20px] lg:!end-[-40px] lg:!start-auto"
        delay={0.6}
        floatDelay={0}
      />
      <FloatingCard
        icon="★"
        tone="gold"
        title={t("float2.title")}
        sub={t("float2.sub")}
        className="bottom-[6%] start-[-8px] sm:start-[-20px] lg:!start-[-40px] lg:!end-auto"
        delay={1.0}
        floatDelay={0.8}
      />
      <FloatingCard
        icon="♥"
        tone="pink"
        title={t("float3.title")}
        sub={t("float3.sub")}
        className="top-[42%] hidden start-[-30px] lg:block"
        delay={1.4}
        floatDelay={1.4}
      />
    </div>
  );
}

/* ---------- Chat bubbles ---------- */

function BotBubble({ text }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 1.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="absolute top-[8%] start-[6%] z-20 rounded-2xl rounded-br-md bg-teal-soft px-3.5 py-2 text-[12.5px] font-medium text-teal-900 shadow-card sm:text-[13px] dark:bg-teal-2/20 dark:text-teal-3"
    >
      <span className="me-1.5 inline-flex align-middle">
        <TypingDots />
      </span>
      {text}
    </motion.div>
  );
}

function UserBubble({ text }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="absolute bottom-[10%] end-[4%] z-20 rounded-2xl rounded-bl-md bg-gradient-to-br from-gold to-gold-2 px-3.5 py-2 text-[12.5px] font-medium text-amber-950 shadow-card sm:text-[13px]"
    >
      {text}
    </motion.div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-[3px]">
      {[0, 1, 2].map((i) => (
        <motion.i
          key={i}
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.2,
            ease: "easeInOut",
          }}
          className="inline-block h-1 w-1 rounded-full bg-teal-2"
        />
      ))}
    </span>
  );
}